// src/hooks/useTelemetry.ts
import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { telemetryManager } from '../telemetry/TelemetryManager';
import type { ModuleName } from '../telemetry/constants/modules';
import type { ActionName } from '../telemetry/constants/actions';

interface UseTelemetryOptions {
  module: ModuleName;
  submodule?: string;
}

export function useTelemetry(defaults: UseTelemetryOptions) {
  const location = useLocation();
  const pageViewTracked = useRef(false);

  // Sync focus to manager whenever the hook is used/rendered
  useEffect(() => {
    telemetryManager.setFocus({
      moduleName: defaults.module,
      submoduleName: defaults.submodule || 'Unknown',
      routePath: location.pathname,
    });
  }, [defaults.module, defaults.submodule, location.pathname]);

  const trackAction = useCallback((
    actionType: ActionName,
    actionLabel: string,
    metadata?: Record<string, unknown>
  ) => {
    telemetryManager.track({
      moduleName: defaults.module,
      submoduleName: defaults.submodule || 'Unknown',
      routePath: location.pathname,
      eventType: 'action',
      actionType,
      actionLabel,
      metadata,
    });
  }, [defaults.module, defaults.submodule, location.pathname]);

  const trackPageView = useCallback((metadata?: Record<string, unknown>) => {
    telemetryManager.track({
      moduleName: defaults.module,
      submoduleName: defaults.submodule || 'Unknown',
      routePath: location.pathname,
      eventType: 'page_view',
      actionType: 'page_view',
      actionLabel: `${defaults.module} Page View`,
      metadata,
    });
  }, [defaults.module, defaults.submodule, location.pathname]);

  // Provide an auto-tracker for convenience if component wants it
  // Commented out to prevent duplicate telemetry page_view events (handled globally by TelemetryProvider)
  // useEffect(() => {
  //   if (!pageViewTracked.current) {
  //     trackPageView();
  //     pageViewTracked.current = true;
  //   }
  // }, [trackPageView]);

  return { trackAction, trackPageView };
}
